/**
 * Batch D — CREIF / ATRIS (Realita) / Penta Real Estate Fund (FKI).
 * Kanonické klíče: `creif`, `atris`, `penta` (mapování legacy v `legacy-fund-key-map.ts`).
 * Assety držíme na osvědčených cestách z `public/` (bez nových 404).
 */

import type { BatchASeedRow } from "./base-funds-batch-a.seed";

export const BATCH_D_SEED_ROWS: readonly BatchASeedRow[] = [
  {
    baseFundKey: "creif",
    canonicalName: "CZECH REAL ESTATE INVESTMENT FUND / EFEKTA Real Estate Fund Class CZK",
    displayName: "CREIF",
    provider: "EFEKTA / DRFG",
    manager: "CAIAC Fund Management AG",
    category: "Nemovitostní fond",
    subcategory: "Real assets / retail",
    currency: "CZK",
    isin: "LI0294389098",
    ticker: null,
    riskSRI: null,
    goal:
      "Fond kupuje již fungující obchodní centra a sklady ve střední Evropě. Cílem je dlouhodobě stabilní výnos s menším kolísáním než u akciových fondů.",
    strategy:
      "Investuje hlavně do komerčních nemovitostí, které přinášejí pravidelné nájemné. Rozkládá riziko mezi různé lokality, typy budov a nájemce; velkou část portfolia tvoří obchody a logistika.",
    description:
      "Nemovitostní fond dostupný pro běžné investory. Peníze vydělává hlavně z nájmů komerčních objektů. Od ledna 2026 se oficiálně jmenuje EFEKTA Real Estate Fund; v Aidvisoře zůstává i starší název CREIF.",
    suitable:
      "Pro opatrnější investory, kteří chtějí mít „kousek nemovitostí“ v portfoliu, aniž by museli sami kupovat konkrétní dům.",
    horizon: "4+ roky",
    liquidity:
      "Peníze lze průběžně vkládat i vybírat, ale protože fond drží nemovitosti, nejde o peníze okamžitě – nemovitosti nelze prodat tak rychle jako akcie.",
    risks:
      "Hodnota nemovitostí může klesat, prodej nemovitostí může trvat, mohou se změnit daně a pravidla. Fond je hodně zaměřený na komerční nemovitosti jako jsou obchody a sklady.",
    minInvestment: null,
    planningRate: 5,
    officialPerformance: {
      ytd: null,
      oneYear: null,
      threeYearPA: null,
      fiveYearPA: null,
      tenYearPA: null,
      sinceInceptionPA: "5.05% (derived from official 64.52% cumulative since 2016-02-23)",
      asOf: "2026-04-05",
    },
    benefits: [
      "Získáte podíl na portfoliu obchodních center a skladů ve střední Evropě.",
      "Stabilní dlouhodobý výnos opřený o pravidelné nájmy.",
      "Srozumitelná investice do „cihel“ – bez nutnosti kupovat vlastní nemovitost.",
    ],
    parameters: {
      fundType: "Otevřený podílový fond / real estate",
      launchDate: "2016-02-23",
      recommendedHorizon: "4 roky",
      officialCumulativeSinceLaunch: "64.52%",
      propertiesValue: "10.48 mld. CZK",
      projectsCount: "26",
      noteOnBrand: "Od 2026-01-19 oficiálně komunikováno jako EFEKTA Real Estate Fund.",
    },
    topHoldings: [
      "Olomouc CITY",
      "SC Haná Olomouc",
      "MMG Centers Szczecin",
      "Retail Park Kutno",
      "JYSK Vyškov",
      "JYSK Jindřichův Hradec",
    ],
    countries: ["Česko", "Polsko"],
    sectors: ["Retail parky a obchodní centra", "Lehká logistika", "Komerční nemovitosti"],
    morningstarRating: null,
    awards: [],
    factsheetUrl: "https://czech-fund.cz/",
    factsheetAsOf: "2026-04-05",
    verifiedAt: "2026-04-05",
    logo: "/logos/funds/creif.png",
    heroImage: "/report-assets/creif/creif-816.jpg",
    galleryImages: [
      "/report-assets/creif/creif-818.jpg",
      "/report-assets/creif/creif-853.jpg",
      "/report-assets/creif/creif-813.jpg",
    ],
    sources: [
      {
        label: "Official product page (current brand)",
        url: "https://czech-fund.cz/",
        kind: "landing_page",
      },
      {
        label: "Official brochure with ISIN and horizon",
        url: "https://czech-fund.cz/wp-content/uploads/2023/08/202510_CZF-CREIF-brozura-CZK-DIP_01_FINAL.pdf",
        kind: "factsheet",
      },
      {
        label: "Official article about rebrand to EFEKTA Real Estate Fund",
        url: "https://czech-fund.cz/",
        kind: "documents",
      },
    ],
    assetTodo: [],
    notes: [
      "Legacy `productKey` zůstává `creif`; oficiální název zahrnuje EFEKTA Real Estate Fund.",
      "Since inception p.a. je dopočteno z oficiální kumulativní výkonnosti 64.52 % od založení.",
      "planningRate 5 % je interní modelový předpoklad pro projekce, ne oficiální performance.",
    ],
  },
  {
    baseFundKey: "atris",
    canonicalName: "Realita nemovitostní otevřený podílový fond, ATRIS investiční společnost, a.s.",
    displayName: "ATRIS",
    provider: "ATRIS investiční společnost",
    manager: "ATRIS investiční společnost, a.s.",
    category: "Nemovitostní fond",
    subcategory: "Real assets / retail",
    currency: "CZK",
    isin: "CZ0008473139",
    ticker: null,
    riskSRI: 3,
    goal:
      "Fond kupuje hlavně obchodní a kancelářské budovy s cílem dlouhodobě a stabilně zhodnocovat peníze investorů.",
    strategy:
      "Drží kvalitní komerční nemovitosti s dlouhými nájemními smlouvami a nízkými dluhy. Zaměřuje se na služby, zdravotnictví, kanceláře a vybrané pražské lokality.",
    description:
      "Český nemovitostní fond s dlouhou historií. Jeho výhodou je nízké zadlužení, skoro plná obsazenost budov a dlouhé nájemní smlouvy.",
    suitable:
      "Pro klienty, kteří chtějí mít v portfoliu nemovitosti s menším kolísáním než u akcií a preferují český fond s reálnými budovami.",
    horizon: "min. 5 let",
    liquidity: "Hodnota podílu se přepočítává jednou měsíčně; peníze z fondu lze vybrat, ale ne okamžitě jako z běžného účtu.",
    risks:
      "Prodej nemovitostí může trvat déle, ceny komerčních budov mohou klesat, fond se zaměřuje na vybraná odvětví a města. Výnos závisí i na tom, jestli jsou budovy pronajaté.",
    minInvestment: "od 500 Kč",
    planningRate: 5,
    officialPerformance: {
      ytd: "0.31%",
      oneYear: "4.02%",
      threeYearPA: null,
      fiveYearPA: null,
      tenYearPA: null,
      sinceInceptionPA: "4.71%",
      asOf: "2026-02-28",
    },
    benefits: [
      "Nízké LTV a vysoká obsazenost portfolia.",
      "Dlouhodobé nájemní smlouvy s WAULT 7.7 roku.",
      "Historie fondu od roku 2009 a zaměření na reálná aktiva.",
    ],
    parameters: {
      fundType: "Speciální fond nemovitostí, OPF",
      depositary: "Československá obchodní banka, a.s.",
      launchDate: "2009-04-27",
      minimumInvestment: "500 Kč",
      managementFee: "1.60 % z výše vlastního kapitálu",
      valuationFrequency: "měsíčně",
      officialNAV: "2.1607 CZK",
      assets: "6 879 693 294 Kč",
      equity: "6 672 830 233 Kč",
      ltv: "28.82%",
      occupancy: "99%",
      wault: "7.7 let",
      portfolioProperties: "15",
    },
    topHoldings: [
      "Obchodní dům Máj na Národní třídě",
      "Palác Bristol v centru Prahy",
      "Delta Ruzyně",
      "Prémiové komerční nemovitosti v Praze",
      "Nemovitosti se zaměřením na služby a zdravotnictví",
    ],
    countries: ["Česko"],
    sectors: ["Služby", "Maloobchod", "Administrativa", "Výroba", "Zdravotnictví"],
    morningstarRating: null,
    awards: [],
    factsheetUrl: "https://storage.atrisinvest.cz/actual/ATRIS_factsheet.pdf",
    factsheetAsOf: "2026-02-28",
    verifiedAt: "2026-04-05",
    logo: "/logos/funds/atris.png",
    heroImage: "/report-assets/atris/atris1.jpg",
    galleryImages: [
      "/report-assets/atris/atris2.jpg",
      "/report-assets/atris/atris3.jpg",
      "/report-assets/atris/atris4.jpg",
    ],
    sources: [
      {
        label: "Official fund page",
        url: "https://www.atrisinvest.cz/fond-realita/",
        kind: "landing_page",
      },
      {
        label: "Official value/performance page",
        url: "https://www.atrisinvest.cz/fond-realita/hodnota-podiloveho-listu/",
        kind: "factsheet",
      },
      {
        label: "Official properties page",
        url: "https://www.atrisinvest.cz/nemovitosti/",
        kind: "documents",
      },
    ],
    assetTodo: [],
    notes: [
      "Jeden kanonický záznam `atris`; lump vs. monthly řeší `InvestmentEntry.type`, ne duplicitní base fondy.",
      "Alias `atris_realita` → `atris` v legacy mapě pro starší exporty.",
      "planningRate 5 % je interní modelový předpoklad pro projekce, ne oficiální performance.",
    ],
  },
  {
    baseFundKey: "penta",
    canonicalName: "Penta Real Estate Fund SICAV, a.s. – Penta Real Estate podfond",
    displayName: "Penta",
    provider: "Penta Fund",
    manager: "CODYA investiční společnost, a.s.",
    category: "Fond kvalifikovaných investorů",
    subcategory: "Real estate / qualified investors",
    currency: "CZK",
    isin: "CZ0008053691",
    ticker: null,
    riskSRI: 6,
    goal:
      "Fond investuje peníze hlavně do nemovitostí a výstavby, které v Česku a na Slovensku vlastní a připravuje skupina Penta. Cílem je dlouhodobě peníze zhodnocovat.",
    strategy:
      "Fond drží jak dokončené pronajímané budovy, tak projekty ve výstavbě. Počítá s delším investičním horizontem a vyšším kolísáním hodnoty než běžné realitní fondy pro veřejnost.",
    description:
      "Nemovitostní fond pro bonitnější klienty, který investuje do staveb skupiny Penta v Česku a na Slovensku. Není to běžný fond pro drobné investory – hodí se jako doplněk už rozvinutého portfolia.",
    suitable:
      "Charakteristika: nemovitostní strategie s delším horizontem, vyšší minimální investicí a omezenou okamžitou likviditou.",
    horizon: "5+ let",
    liquidity:
      "Hodnota fondu se přepočítává jednou měsíčně. Peníze z fondu lze vybrat, ale jejich skutečné vyplacení může trvat až 24 měsíců.",
    risks:
      "Vyšší riziko než u běžných realitních fondů, silná vazba na projekty Penty, omezená možnost rychle vybrat peníze, využití úvěrů a rizika výstavby i ekonomického cyklu.",
    minInvestment: "od 1 000 000 Kč / 50 000 EUR",
    planningRate: 13,
    officialPerformance: {
      ytd: null,
      oneYear: null,
      threeYearPA: null,
      fiveYearPA: null,
      tenYearPA: null,
      sinceInceptionPA: null,
      asOf: "2026-04-05",
    },
    benefits: [
      "Možnost podílet se na konkrétních nemovitostech a stavbách skupiny Penta.",
      "Potenciálně vyšší výnos, než nabízejí běžné realitní fondy pro širokou veřejnost.",
      "Dává smysl jako doplněk portfolia pro klienty s větším majetkem.",
    ],
    parameters: {
      fundType: "Qualified investor fund / SICAV sub-fund",
      subFundLaunch: "2024-10-24",
      cnbRegistrationFund: "2024-10-07",
      cnbRegistrationSubFund: "2024-10-30",
      targetYield: "13 - 15 % p.a.",
      minimumInvestment: "1 000 000 Kč / 50 000 EUR",
      portfolioGrossAssetValue: "€1.75B",
      managementFeeCurrentClassD: "1.6 % p.a.",
      recommendedHoldingPeriod: "5 let",
      redemptionSettlement: "až 24 měsíců",
      entryFeeMax: "5%",
      exitFee: "až 20 % v prvních 3 letech, poté 0 %",
    },
    topHoldings: [
      "Masaryčka",
      "Nová Waltrovka",
      "Florenc 21",
      "4. kvadrant",
      "Nuselský pivovar",
      "Vinohradská 8",
      "Rezidence Petrská",
      "Southbank",
      "Chalupkova",
      "Vajnorská",
      "Medze",
    ],
    countries: ["Česko", "Slovensko"],
    sectors: ["Commercial real estate", "Residential real estate", "Development"],
    morningstarRating: null,
    awards: ["Masaryčka – nejlepší stavba roku 2024 (uvedeno v term sheetu / dokumentaci)"],
    factsheetUrl: "https://www.pentafund.com/storage/FACTSHEET_Penta-Real-Estate-Fund_CZ.pdf",
    factsheetAsOf: "2026-04-05",
    verifiedAt: "2026-04-05",
    logo: "/logos/funds/penta.png",
    heroImage: "/report-assets/penta/hero.svg",
    galleryImages: [
      "/report-assets/penta/penta1.png",
      "/report-assets/penta/penta2.jpg",
      "/report-assets/penta/penta3.webp",
    ],
    sources: [
      {
        label: "Official product page",
        url: "https://www.pentafund.com/penta-real-estate-fund/",
        kind: "landing_page",
      },
      {
        label: "Official Czech factsheet",
        url: "https://www.pentafund.com/storage/FACTSHEET_Penta-Real-Estate-Fund_CZ.pdf",
        kind: "factsheet",
      },
      {
        label: "Official KID Class D",
        url: "https://www.pentafund.com/storage/KID-Class-D_Penta-Real-Estate-Fund_EN.pdf",
        kind: "kid",
      },
      {
        label: "Official statute / term sheet reference",
        url: "https://www.pentafund.com/storage/STATUT_Penta-Real-Estate-Fund_CZ.pdf",
        kind: "documents",
      },
    ],
    assetTodo: ["hero (volitelně nahradit SVG reálným snímkem)"],
    notes: [
      "Tag `qualified_investor` v availability — filtrování FKI; oddělená UI skupina „FKI / alternativní“.",
      "Oficiální historická performance v seedu záměrně null; target yield není performance.",
      "planningRate 13 % je interní modelový předpoklad odvozený z pásma 13–15 % p.a., ne historická výkonnost.",
      "Alias `penta_real_estate_fund` → `penta` pro importy ze seed dokumentů.",
    ],
  },
];
